/**
 * POV presets — the layer model's closed extension point.
 *
 * PRODUCT_PLAN.md P3: "A POV is a preset — a named bundle of {structure,
 * token values, default slots} — and nothing else." The test that proves the
 * model hasn't leaked (F.4): adding a POV must touch no renderer.
 *
 * `Drop.pov` stays free text (`Drop.ts:1706-1711`) — presets will outgrow
 * this list, and a Drop whose preset was retired must keep rendering. This
 * map is the known set today; an unrecognized or retired `pov` falls back to
 * CREATOR's preset, never throws.
 */

import {DropType} from "@devour/client";
import {SURFACE_STYLE} from "./resolveStyle";
import {BrandTokens} from "./brandTokens";
import {SurfaceSlot, SlotRole, SlotFootprint} from "./slots";

export interface PovPreset {
    structure: (typeof SURFACE_STYLE)[keyof typeof SURFACE_STYLE];
    /** Corpus-neutral fallback — a Drop's own brand token file (Phase B) overrides these. */
    tokens: BrandTokens;
    defaultSlots: SurfaceSlot[];
    /**
     * What the Stage shows when nothing is playing. "brandPanel" is the
     * reduced lockup panel (`StageBrandPanel`); "identity" is `SurfaceStage`'s
     * full identity block — phase line, lockup, synopsis, CTAs, stats, faces.
     * A structural choice, so it lives on the preset (stageWidgets §2.5b calls
     * the panel "one campaign's choice" — this is where that choice is made).
     */
    stageIdle: "brandPanel" | "identity";
    /**
     * Preset-level custom properties, spread UNDER the brand's own record so
     * an authored value always wins. The spine's rules read these with the
     * publisher look as the CSS fallback, so presets that don't set them are
     * byte-identical.
     */
    cssVars?: Record<string, string>;
    /**
     * Class the surface wrapper carries for this POV, so a preset's CSS
     * refinements can scope to it instead of editing the SHARED spine —
     * unscoped "parity" edits to HubSpine are the recorded MW4 regression.
     */
    rootClass?: string;
}

/**
 * `design-guide.html`'s own values — the corpus contract's neutral default,
 * not invented. A specific Drop's brand token file (`brandTokens.ts`)
 * supplies the real palette; this is only what renders before one is set.
 */
const NEUTRAL_TOKENS: BrandTokens = {
    bg: "#050505",
    surface: "#16161b",
    accent: "#F1161D",
    accentInk: "#f0f0f3",
    text: "#f0f0f3",
};

/**
 * The slot template a hub-shaped POV starts from — role + footprint only, no
 * occupant. Read off `publisher-drop-hub.html`'s spine (`PRODUCT_PLAN.md`
 * §2.3, `FLUIDITY_CONTRACT.md` §4/§7): STAGE and NAV are unconditional;
 * FEATURE is the contested column; ENGAGEMENT is the journey/bingo column
 * widget; COLLECTION is the games/rewards/roster horizontal rail; STATEMENT
 * is the earn-rules band. Every slot is optional in practice — RTTR drops 36
 * of the publisher spine's 200 classes and still renders (`slots.ts` rule 3)
 * — this is the full spine a POV can draw from, not a requirement that all
 * of it appear.
 */
const HUB_SPINE: SurfaceSlot[] = [
    {id: "stage", role: SlotRole.STAGE, footprint: SlotFootprint.STAGE},
    {id: "nav", role: SlotRole.NAV, footprint: SlotFootprint.RAIL},
    {id: "feature", role: SlotRole.FEATURE, footprint: SlotFootprint.COLUMN},
    {id: "engagement", role: SlotRole.ENGAGEMENT, footprint: SlotFootprint.COLUMN_WIDGET},
    {id: "collection", role: SlotRole.COLLECTION, footprint: SlotFootprint.HORIZONTAL_RAIL},
    {id: "statement", role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
];

/**
 * PAGE-shaped POVs get the stage and a scrolling statement band — no
 * contested column, no rail spine. `FLUIDITY_CONTRACT.md` §9 documents PAGE's
 * own rules as still to be written; this is deliberately minimal until they are.
 */
const PAGE_SPINE: SurfaceSlot[] = [
    {id: "stage", role: SlotRole.STAGE, footprint: SlotFootprint.STAGE},
    {id: "statement", role: SlotRole.STATEMENT, footprint: SlotFootprint.BAND},
];

/*
 * ── THE SPINE IS SHARED. THE FIELDS ARE NOT DECLARED HERE AT ALL. ──
 *
 * This block held `ESPORTS_FIELDS` / `COLLEGIATE_FIELDS` / `AGENCY_FIELDS` as
 * source constants, with esports and collegiate split into their own presets
 * purely to carry different ones. The schema is authored data now and lives on
 * `StreamingTeam.rosterFields` — the reasoning is in
 * `packages/models/src/streaming/RosterFields.ts`, told once.
 *
 * What remains here is the SPINE, shared by every POV. The create-time
 * templates are in `rosterFieldTemplates.ts`.
 */
const HUB_PRESET: PovPreset = {structure: SURFACE_STYLE.HUB, tokens: NEUTRAL_TOKENS, defaultSlots: HUB_SPINE, stageIdle: "brandPanel"};
const PAGE_PRESET: PovPreset = {structure: SURFACE_STYLE.PAGE, tokens: NEUTRAL_TOKENS, defaultSlots: PAGE_SPINE, stageIdle: "brandPanel"};
/**
 * The BRAND hub anchor (rttf-drop-hub.html) rests on the FULL identity stage,
 * not the panel, and its CTA is the flat square accent block — not the
 * publisher's rounded gradient pill.
 */
const BRAND_HUB_PRESET: PovPreset = {
    ...HUB_PRESET,
    stageIdle: "identity",
    cssVars: {
        "--cta-bg": "var(--o)",
        "--cta-r": "0",
        "--cta-ink": "var(--octa, #ffffff)",
        /*
         * The brand notch on earn cards — the anchor's --ml-bevel. The
         * widget's own default reads this token; setting --earn-bevel here
         * directly would lose to that element-level default.
         */
        "--surface-earn-bevel": "18px",
    },
    rootClass: "pov-brand",
};

/**
 * PUBLISHER / FRANCHISE / GAME ARE ONE POV — Shelly, 2026-08-23
 * (`BUILD_PLAN.md` §9b): depths of `Drop.parentDropId`, not three layouts.
 * All three point at the identical preset object on purpose — a franchise
 * page that diverges from a title page would be a bug in the preset, not a
 * reason for a third one.
 *
 * COMMUNITY and AGENCY have no hub anchor yet (`PRODUCT_PLAN.md` §3, §9) —
 * PAGE until one exists, not a guess at what their hub form would be.
 */
export const POV_PRESETS: Record<DropType, PovPreset> = {
    [DropType.CREATOR]: PAGE_PRESET,
    [DropType.PUBLISHER]: HUB_PRESET,
    [DropType.FRANCHISE]: HUB_PRESET,
    [DropType.GAME]: HUB_PRESET,
    [DropType.BRAND]: BRAND_HUB_PRESET,
    [DropType.ESPORTS_PROFESSIONAL]: HUB_PRESET,
    [DropType.ESPORTS_COLLEGIATE]: HUB_PRESET,
    [DropType.COMMUNITY]: PAGE_PRESET,
    [DropType.AGENCY]: PAGE_PRESET,
};


/**
 * Never throws. `pov` is free text on the Drop (`Drop.ts:1706-1711`), not a
 * `DropType` — a retired or not-yet-recognized value falls back to CREATOR's
 * preset rather than crash a page.
 */
export function povPreset(pov: string | null | undefined): PovPreset {
    if (!pov) return POV_PRESETS[DropType.CREATOR];
    return (POV_PRESETS as Record<string, PovPreset>)[pov] ?? POV_PRESETS[DropType.CREATOR];
}
